import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import {
  UnleashProvider,
  useFlag,
  useFlagsStatus,
  useOriginIdentity,
} from "@origin-digital/unleash";
import { appName, config } from "../../config";
import { useProfileFieldMapChooser } from "../../crm/ProfileFieldMapContext";
import { ProfileFieldMapTypes } from "../../crm/profileFieldMapContextHelpers";
import { useJointAccountHolderEligibility } from "../../crm/kraken/jointAccountHolderQuery";

type Access =
  import("../../crm/kraken/jointAccountHolderEligibility").JointAccountHolderEligibilityResult;
const DENIED: Access = { eligible: false, hasHardship: false, accounts: [] };
const JointAccountHolderAccess = createContext<Access>(DENIED);
export const useJointAccountHolderAccess = () =>
  useContext(JointAccountHolderAccess);

const AccessResolver = ({
  onChange,
}: {
  onChange: (access: Access) => void;
}) => {
  const flag = useFlag(config.unleash.flags.jointAccountHolder);
  const { flagsReady, flagsError } = useFlagsStatus();
  const { metadata } = useProfileFieldMapChooser();
  const hasKraken = [
    ProfileFieldMapTypes.KRAKEN,
    ProfileFieldMapTypes.MULTI_CRMS,
  ].includes(metadata.type);
  const enabled = flagsReady && !flagsError && flag && hasKraken;
  const result = useJointAccountHolderEligibility(enabled);
  const eligible =
    enabled && result.eligible && !result.loading && !result.error;
  const hasHardship = eligible && result.hasHardship;
  const accounts = eligible
    ? result.accounts ?? DENIED.accounts
    : DENIED.accounts;
  useLayoutEffect(() => {
    onChange({ eligible, hasHardship, accounts });
    return () => onChange(DENIED);
  }, [eligible, hasHardship, accounts, onChange]);
  return null;
};

/** Resolve flags alongside the app: identity/flag failures never hide profile editing. */
const EnabledJointAccountHolderAccessProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const identity = useOriginIdentity();
  const [access, setAccess] = useState<Access>(DENIED);
  return (
    <JointAccountHolderAccess.Provider
      value={identity.userId ? access : DENIED}
    >
      {identity.userId && (
        <UnleashProvider
          key={identity.userId}
          config={{ appName, environment: config.unleash.environment }}
          context={identity}
        >
          <AccessResolver onChange={setAccess} />
        </UnleashProvider>
      )}
      {children}
    </JointAccountHolderAccess.Provider>
  );
};

/** Local development does not resolve identity or contact the flag service. */
export const JointAccountHolderAccessProvider = ({
  children,
}: {
  children: React.ReactNode;
}) =>
  config.unleash.enabled ? (
    <EnabledJointAccountHolderAccessProvider>
      {children}
    </EnabledJointAccountHolderAccessProvider>
  ) : (
    <JointAccountHolderAccess.Provider value={DENIED}>
      {children}
    </JointAccountHolderAccess.Provider>
  );
